import type { Rule } from '@guard-dog/contracts';
import { type FormEvent, useState } from 'react';
import { ApiError, api, type ConsolePrincipal } from '../api/client';
import { Async } from '../components/Async';
import {
  buildRule,
  describeAction,
  RATE_LIMIT_DEFAULTS,
  type RuleKind,
  summarizeMatch,
  toggleRule,
} from '../rules/buildRule';
import { useFetch } from '../useFetch';

const KINDS: { value: RuleKind; label: string }[] = [
  { value: 'block-path', label: 'Block a path' },
  { value: 'allow-path', label: 'Allow a path' },
  { value: 'block-ip', label: 'Block an IP / CIDR' },
  { value: 'allow-ip', label: 'Allow an IP / CIDR' },
  { value: 'rate-limit-path', label: 'Rate-limit POSTs to a path' },
];

/**
 * Rules management: the tenant's authored rules (any role can read) with an enable/disable
 * kill switch, plus guided authoring for the common shapes (owner/admin). Both write paths go
 * through POST /v1/console/rules, which upserts + auto-distributes — a kill reaches the edge
 * on the same call.
 */
export function Rules({
  principal,
  canEdit,
  canAddProtection,
}: {
  principal: ConsolePrincipal | null;
  canEdit: boolean;
  canAddProtection: boolean;
}) {
  const sites = useFetch(() => api.sites());
  const rules = useFetch(() => api.listRules());
  const [kind, setKind] = useState<RuleKind>('block-path');
  const [target, setTarget] = useState('');
  const [scope, setScope] = useState<'tenant' | 'site'>('tenant');
  const [siteId, setSiteId] = useState('');
  const [description, setDescription] = useState('');
  // Kept as strings so the inputs can be cleared: an empty ipLimit is a real choice (no ceiling),
  // and coercing "" to 0 would author a rule the validator rejects.
  const [limit, setLimit] = useState(String(RATE_LIMIT_DEFAULTS.limit));
  const [windowSec, setWindowSec] = useState(String(RATE_LIMIT_DEFAULTS.windowSec));
  const [ipLimit, setIpLimit] = useState(String(RATE_LIMIT_DEFAULTS.ipLimit));
  const [then, setThen] = useState<'block' | 'challenge'>(RATE_LIMIT_DEFAULTS.then);
  const [pending, setPending] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isIp = kind.endsWith('-ip');
  const isRate = kind === 'rate-limit-path';

  function fail(err: unknown, fallback: string) {
    setError(
      err instanceof ApiError && (err.status === 401 || err.status === 403)
        ? 'Not authorized.'
        : fallback,
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!principal || !target.trim() || pending || !canAddProtection) return;
    if (scope === 'site' && !siteId) {
      setError('Pick a site.');
      return;
    }
    if (isRate) {
      // Caught here so the operator gets a pointed message instead of a 400 from the validator.
      if (!(Number(limit) > 0) || !(Number(windowSec) > 0)) {
        setError('Limit and window must be positive numbers.');
        return;
      }
      if (ipLimit.trim() !== '' && !(Number(ipLimit) > 0)) {
        setError('IP ceiling must be a positive number, or empty for no ceiling.');
        return;
      }
    }
    setPending(true);
    setError(null);
    setResult(null);
    try {
      const rule = buildRule({
        kind,
        target,
        scope,
        tenantId: principal.tenantId,
        siteId: siteId || undefined,
        description,
        ...(isRate
          ? {
              limit: Number(limit),
              windowSec: Number(windowSec),
              ipLimit: ipLimit.trim() === '' ? undefined : Number(ipLimit),
              then,
            }
          : {}),
      });
      const res = await api.authorRule(rule);
      setResult(`Authored ${res.ruleId} — distributed to ${res.distributed} site(s).`);
      setTarget('');
      setDescription('');
      rules.reload();
    } catch (err) {
      fail(err, 'Failed to author rule.');
    } finally {
      setPending(false);
    }
  }

  async function toggle(rule: Rule) {
    if (togglingId) return;
    if (!rule.enabled && !canAddProtection) return;
    setTogglingId(rule.id);
    setError(null);
    setResult(null);
    try {
      const res = await api.authorRule(toggleRule(rule));
      setResult(
        `${rule.enabled ? 'Disabled' : 'Enabled'} ${rule.id} — distributed to ${res.distributed} site(s).`,
      );
      rules.reload();
    } catch (err) {
      fail(err, 'Failed to update rule.');
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <section>
      <h2>Rules</h2>

      <Async state={rules}>
        {({ rules: list }) =>
          list.length === 0 ? (
            <p className="muted">No rules authored yet.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Match</th>
                  <th>Action</th>
                  <th>Scope</th>
                  <th>Status</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className={r.enabled ? '' : 'disabled-row'}>
                    <td>
                      <span className="mono">{r.id}</span>
                      {r.meta.description && <div className="muted">{r.meta.description}</div>}
                    </td>
                    <td className="mono">{summarizeMatch(r.match)}</td>
                    <td>{describeAction(r.action)}</td>
                    <td className="muted">{r.scope}</td>
                    <td>
                      <span className={`badge badge-${r.enabled ? 'on' : 'off'}`}>
                        {r.enabled ? 'enabled' : 'disabled'}
                      </span>
                    </td>
                    {canEdit && (
                      <td>
                        <button
                          type="button"
                          className="link-button"
                          disabled={togglingId !== null || (!r.enabled && !canAddProtection)}
                          title={
                            !r.enabled && !canAddProtection
                              ? 'Enabling rules is paused until the plan is active.'
                              : undefined
                          }
                          onClick={() => toggle(r)}
                        >
                          {togglingId === r.id ? '…' : r.enabled ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Async>

      {result && <p className="ok">{result}</p>}
      {error && <p className="error">{error}</p>}

      {canEdit ? (
        <>
          <h3>Author a rule</h3>
          {canAddProtection ? (
            <p className="muted">It auto-distributes to affected edge sites.</p>
          ) : (
            <p className="restriction-note">
              Rule authoring and re-enabling are paused until the plan is active. You can still
              disable existing rules.
            </p>
          )}

          <form className="form" onSubmit={submit}>
            <label>
              Rule
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as RuleKind)}
                disabled={!canAddProtection}
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              {isIp ? 'IP / CIDR' : 'Path'}
              <input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder={isIp ? '198.51.100.0/24' : '/xmlrpc.php  or  /wp-admin/*'}
                disabled={!canAddProtection}
              />
            </label>

            {isRate && (
              <>
                <p className="muted">
                  Counts POSTs only, so visiting the page is never limited. Two budgets apply and
                  exceeding either one acts: the per-account budget stops guessing at one login, and
                  the IP ceiling stops someone trying many different logins from one address.
                </p>

                <label>
                  Attempts per account
                  <input
                    type="number"
                    min="1"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    disabled={!canAddProtection}
                  />
                </label>

                <label>
                  Window (seconds)
                  <input
                    type="number"
                    min="1"
                    value={windowSec}
                    onChange={(e) => setWindowSec(e.target.value)}
                    disabled={!canAddProtection}
                  />
                </label>

                <label>
                  IP ceiling (blank = none)
                  <input
                    type="number"
                    min="1"
                    value={ipLimit}
                    onChange={(e) => setIpLimit(e.target.value)}
                    placeholder="20"
                    disabled={!canAddProtection}
                  />
                </label>
                <p className="muted">
                  Leaving this blank counts by address alone, so everyone sharing an office or
                  campus connection draws from one budget and can lock each other out.
                </p>

                <label>
                  On exceeding
                  <select
                    value={then}
                    onChange={(e) => setThen(e.target.value as 'block' | 'challenge')}
                    disabled={!canAddProtection}
                  >
                    <option value="block">Block</option>
                    <option value="challenge">Challenge</option>
                  </select>
                </label>
              </>
            )}

            <label>
              Scope
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as 'tenant' | 'site')}
                disabled={!canAddProtection}
              >
                <option value="tenant">Whole tenant</option>
                <option value="site">One site</option>
              </select>
            </label>

            {scope === 'site' && (
              <label>
                Site
                <select
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  disabled={!canAddProtection}
                >
                  <option value="">{sites.loading ? 'Loading…' : 'Select…'}</option>
                  {(sites.data?.sites ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.url}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label>
              Description (optional)
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canAddProtection}
              />
            </label>

            <button type="submit" disabled={pending || !target.trim() || !canAddProtection}>
              {pending ? 'Authoring…' : 'Author rule'}
            </button>
          </form>
        </>
      ) : (
        <p className="muted">You need an owner or admin role to author or toggle rules.</p>
      )}
    </section>
  );
}
